import clsx from "clsx";
import { FC, memo, ReactNode, useState } from "react";

import type {
  ContentImage,
  ContentText,
  ContentToolUse,
} from "@tsmono/inspect-common/types";
import {
  CopyButton,
  ExpandablePanel,
  LabeledValue,
  MarkdownDiv,
  type MarkdownReference,
} from "@tsmono/react/components";

import { useDisplayMode } from "../content/DisplayModeContext";
import { RecordTree } from "../content/RecordTree";

import styles from "./ChatMessage.module.css";
import { MessageContent } from "./MessageContent";
import { MessageContents } from "./MessageContents";
import { hasServerToolUse, Message } from "./messages";
import { ServerToolCall } from "./server-tools/ServerToolCall";
import {
  codexToolMarkdown,
  formatSubagentNotifications,
  parseToolSearchCatalog,
  type ToolSearchNamespaceEntry,
} from "./tools/tool";
import { ToolOutput } from "./tools/ToolOutput";
import { ToolSearchView } from "./tools/ToolSearchView";
import { ChatViewDisplayOptions, ChatViewLinkingOptions } from "./types";

interface ChatMessageProps {
  id: string;
  message: Message;
  display?: ChatViewDisplayOptions;
  linking?: ChatViewLinkingOptions;
  references?: MarkdownReference[];
  /** Optional position-label chip, rendered at the far right of the role line. */
  label?: ReactNode;
  /** Optional leading element rendered before the role text (e.g. a
   *  selection checkbox while evidence selection mode is on). */
  headerLeading?: ReactNode;
}

export const ChatMessage: FC<ChatMessageProps> = memo(function ChatMessage({
  id,
  message,
  display,
  linking,
  references,
  label,
  headerLeading,
}) {
  const indented = display?.indented ?? false;
  const unlabeledRoles = display?.unlabeledRoles;
  const formatDateTime = display?.formatDateTime;
  const linkingEnabled = linking?.enabled ?? false;
  const getMessageUrl = linking?.getMessageUrl;
  const displayMode = useDisplayMode();

  const messageUrl = getMessageUrl?.(message.id || "");

  const [mouseOver, setMouseOver] = useState(false);

  const isNonSubagentTool =
    message.role === "tool" &&
    message.function !== "Task" &&
    message.function !== "task" &&
    message.function !== "Agent" &&
    message.function !== "agent";
  const hideRole = unlabeledRoles?.includes(message.role) ?? false;

  // Codex tool results get friendlier rendering in rendered mode:
  // tool_search → a collapsible catalog component; sub-agent management answers
  // → markdown. Raw mode keeps the original message content.
  let toolSearchNamespaces: ToolSearchNamespaceEntry[] | undefined;
  let toolMarkdown: string | undefined;
  if (displayMode === "rendered" && isNonSubagentTool && message.function) {
    if (message.function === "tool_search") {
      toolSearchNamespaces = parseToolSearchCatalog(message.content);
    } else {
      toolMarkdown = codexToolMarkdown(message.function, message.content);
    }
  }

  // Codex sub-agent completion notifications (user messages) collapse to a
  // compact status line — the answer itself is shown by the paired wait/close
  // result. Raw mode keeps the original notification.
  const subagentNotifications =
    displayMode === "rendered" && message.role === "user"
      ? formatSubagentNotifications(message.content)
      : undefined;

  // When the role header is hidden, skip rendering if there's no visible
  // content (e.g. assistant messages with only tool_calls).
  if (hideRole) {
    const content = message.content;
    const hasVisibleContent =
      typeof content === "string"
        ? content.trim().length > 0
        : Array.isArray(content) && content.length > 0;
    const hasToolCalls =
      "tool_calls" in message &&
      Array.isArray(message.tool_calls) &&
      message.tool_calls.length > 0;
    if (!hasVisibleContent && !hasToolCalls) {
      return null;
    }
  }

  const roleHeader = !hideRole ? (
    <div
      className={clsx(
        styles.messageGrid,
        message.role === "tool" ? styles.toolMessageGrid : undefined,
        "text-style-label"
      )}
    >
      <div>
        {headerLeading ? (
          <span className={styles.headerLeading}>{headerLeading}</span>
        ) : null}
        {message.role}
        {message.role === "tool"
          ? message.function
            ? `: ${message.function}`
            : ""
          : ""}
        {linkingEnabled && messageUrl ? (
          <CopyButton
            icon="link"
            value={messageUrl}
            className={clsx(styles.copyLink)}
          />
        ) : (
          ""
        )}
      </div>
      {(message.timestamp && formatDateTime) || label ? (
        <div className={styles.headerEnd}>
          {message.timestamp && formatDateTime && (
            <span className={styles.timestamp} title={message.timestamp}>
              {formatDateTime(new Date(message.timestamp))}
            </span>
          )}
          {label}
        </div>
      ) : null}
    </div>
  ) : null;

  const metadataBlock =
    message.metadata && Object.keys(message.metadata).length > 0 ? (
      <LabeledValue
        label="Metadata"
        className={clsx(styles.metadataLabel, "text-size-smaller")}
      >
        <RecordTree
          record={message.metadata}
          id={`${id}-metadata`}
          defaultExpandLevel={0}
          copyButton={true}
        />
      </LabeledValue>
    ) : null;

  // An assistant turn with server-side tool calls renders as one seamless
  // container: prose rows keep the assistant band, each tool_use stacks as a
  // flush tool block row, separated by hairlines.
  const segments = segmentTurnContent(message);
  if (segments) {
    return (
      <div
        data-message-id={message.id || undefined}
        className={clsx(
          message.role,
          "text-size-base",
          styles.message,
          styles.turnSegments,
          mouseOver ? styles.hover : undefined
        )}
        onMouseEnter={() => setMouseOver(true)}
        onMouseLeave={() => setMouseOver(false)}
      >
        {segments.map((segment, index) => {
          if (segment.kind === "tool") {
            return (
              <ServerToolCall
                key={`${id}-segment-${index}`}
                id={`${id}-server-tool-${index}`}
                content={segment.content}
              />
            );
          }
          // An empty leading prose segment still renders when it hosts the
          // role header (the turn starts directly with a tool call).
          if (segment.contents.length === 0 && (index > 0 || hideRole)) {
            return null;
          }
          return (
            <div
              key={`${id}-segment-${index}`}
              data-message-role={message.role}
              className={styles.proseSegment}
            >
              {index === 0 ? roleHeader : null}
              {segment.contents.length > 0 ? (
                <ExpandablePanel
                  id={`${id}-message-${index}`}
                  collapse={true}
                  lines={25}
                >
                  <MessageContent
                    contents={segment.contents}
                    references={references}
                  />
                </ExpandablePanel>
              ) : null}
            </div>
          );
        })}
        {metadataBlock ? (
          <div data-message-role={message.role} className={styles.proseSegment}>
            {metadataBlock}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      data-message-id={message.id || undefined}
      className={clsx(
        message.role,
        "text-size-base",
        styles.message,
        message.role === "system" ? styles.systemRole : undefined,
        mouseOver ? styles.hover : undefined
      )}
      onMouseEnter={() => setMouseOver(true)}
      onMouseLeave={() => setMouseOver(false)}
    >
      {roleHeader}
      <div
        className={clsx(
          styles.messageContents,
          indented ? styles.indented : undefined
        )}
      >
        <ExpandablePanel
          id={`${id}-message`}
          collapse={true}
          lines={
            message.role === "tool"
              ? 30
              : message.role === "assistant"
                ? 25
                : 15
          }
        >
          {isNonSubagentTool ? (
            toolSearchNamespaces ? (
              <ToolSearchView namespaces={toolSearchNamespaces} />
            ) : toolMarkdown !== undefined ? (
              <MarkdownDiv markdown={toolMarkdown} />
            ) : (
              <ToolOutput
                output={
                  typeof message.content === "string"
                    ? message.content
                    : message.content.filter(
                        (c): c is ContentText | ContentImage =>
                          c.type === "text" || c.type === "image"
                      )
                }
              />
            )
          ) : subagentNotifications !== undefined ? (
            <MarkdownDiv markdown={subagentNotifications} />
          ) : (
            <MessageContents
              key={`${id}-contents`}
              message={message}
              references={references}
            />
          )}
        </ExpandablePanel>

        {metadataBlock}
      </div>
    </div>
  );
});

type TurnSegment =
  | { kind: "prose"; contents: Exclude<Message["content"], string> }
  | { kind: "tool"; content: ContentToolUse };

/** Splits an assistant message that carries server-side tool calls into
 * alternating prose / tool-call segments (in content order). Returns
 * undefined for messages that render the regular way. */
const segmentTurnContent = (message: Message): TurnSegment[] | undefined => {
  if (!hasServerToolUse(message) || !Array.isArray(message.content)) {
    return undefined;
  }
  const segments: TurnSegment[] = [{ kind: "prose", contents: [] }];
  for (const item of message.content) {
    if (item.type === "tool_use") {
      segments.push({ kind: "tool", content: item });
    } else {
      const last = segments[segments.length - 1];
      if (last?.kind === "prose") {
        last.contents.push(item);
      } else {
        segments.push({ kind: "prose", contents: [item] });
      }
    }
  }
  return segments;
};
